import { Filters } from "@portal/app/_common/types/dreamSearch";
import { isCuidLike } from "@portal/app/_common/lib/cuid";
import {
  assertIndustry,
  assertPaymentOptionType,
  assertProgramEligibilityRequirement,
  assertProgramEnvironment,
  assertProgramLevel,
  assertProgramTag,
  assertSchoolState,
  assertSubindustry,
  cleanQueryParamValue,
  getFilterBoolean,
  getFilterEnumArray,
  getFilterNumber,
  getFilterString,
  getFilterStringArray,
  getParamArray,
  getParamString,
  getParamStringFromBoolean,
  getParamStringFromNumber,
} from "./queryParamFilterHelpers";

export type KeyToFilterMap = typeof queryParamKeyToFilterKeyMap;
export type QueryParamKey = keyof KeyToFilterMap;
export type QueryParams = Record<QueryParamKey, string | string[] | undefined>;
export type FilterKey = KeyToFilterMap[QueryParamKey];
type ParsedUrlQuery = NodeJS.Dict<string | string[]>;

export type FilterTranslatorFunction<T extends FilterKey = FilterKey> = (
  filterValue: Filters[T]
) => string | string[] | undefined;

// Mapping shortened names of query param keys to the full filter key names
export const queryParamKeyToFilterKeyMap = {
  environment: "program_environment",
  exclude_environment: "program_exclude_environment",
  exclude_requirement: "program_exclude_requirement",
  program_name: "program_name",
  vocations: "program_vocation_in",
  vocation_slugs: "program_vocation_slug_in",
  industry: "program_vocation_industry",
  exclude_schools: "provider_exclude_id_in",
  school_name: "provider_name_search_for",
  school_state: "provider_state_in",
  payment_options: "program_payment_options_type_in",
  program_tags: "program_tags_in",
  program_duration_less_than: "program_duration_days_lte",
  program_level: "program_level_in",
  only_partnered: "provider_only_partnered",
  subindustries: "program_vocation_subindustries_in",
} as const;

/**
 * `?pinned_program=<programId>`. Not a filter, so it stays out of
 * `queryParamKeyToFilterKeyMap`; `updateUrl` retains unrecognized params,
 * which keeps the pin across filter changes.
 */
export const PINNED_PROGRAM_QUERY_PARAM = "pinned_program";

/**
 * Same ad-platform cleaning as the filters, then a strict CUID gate. Repeated
 * keys and malformed percent-escapes (which make decoding throw) read as no pin.
 */
export const getPinnedProgramIdFromQuery = (searchParams: {
  [key: string]: string | string[] | undefined;
}): string | undefined => {
  try {
    const value = cleanQueryParamValue(searchParams[PINNED_PROGRAM_QUERY_PARAM]);
    return isCuidLike(value) ? value : undefined;
  } catch {
    return undefined;
  }
};

// Finding match of query param key to filter key.
const getParamFilterKey = (paramKey: QueryParamKey): FilterKey =>
  queryParamKeyToFilterKeyMap[paramKey];
type ParamTranslatorFunction<T extends QueryParamKey> = (
  queryParam: string | string[] | undefined
) => Filters[KeyToFilterMap[T]];

type ParamTranslator = {
  [K in QueryParamKey]: ParamTranslatorFunction<K>;
};

type FilterTranslator = {
  [K in FilterKey]: FilterTranslatorFunction<K>;
};

// Functions to convert DreamSearch filters to query params
export const translateFiltersToQueryParams: FilterTranslator = {
  program_environment: getParamArray,
  program_exclude_environment: getParamArray,
  program_exclude_requirement: getParamArray,
  program_name: getParamString,
  program_vocation_in: getParamArray,
  program_vocation_slug_in: getParamArray,
  program_vocation_industry: getParamArray,
  program_payment_options_type_in: getParamArray,
  program_tags_in: getParamArray,
  program_duration_days_lte: getParamStringFromNumber,
  program_vocation_subindustries_in: getParamArray,
  provider_exclude_id_in: getParamArray,
  provider_name_search_for: getParamArray,
  provider_state_in: getParamArray,
  program_level_in: getParamArray,
  provider_only_partnered: getParamStringFromBoolean,
} as const;

// Functions to convert query params to DreamSearch filters
const translateQueryParamsToFilters: ParamTranslator = {
  environment: (queryValue) =>
    getFilterEnumArray<GQL.ProgramEnvironment>(queryValue, assertProgramEnvironment),
  exclude_environment: (queryValue) =>
    getFilterEnumArray<GQL.ProgramEnvironment>(queryValue, assertProgramEnvironment),
  exclude_requirement: (queryValue) =>
    getFilterEnumArray<GQL.EligibilityRequirement>(queryValue, assertProgramEligibilityRequirement),
  program_name: getFilterString,
  payment_options: (queryValue) =>
    getFilterEnumArray<GQL.PaymentOptionType>(queryValue, assertPaymentOptionType),
  program_tags: (queryValue) => getFilterEnumArray<GQL.ProgramTag>(queryValue, assertProgramTag),
  program_duration_less_than: getFilterNumber,
  vocations: getFilterStringArray,
  vocation_slugs: getFilterStringArray,
  industry: (queryValue) => getFilterEnumArray<GQL.VocationIndustry>(queryValue, assertIndustry),
  exclude_schools: getFilterStringArray,
  school_name: getFilterStringArray,
  school_state: (queryValue) => getFilterEnumArray<GQL.UsState>(queryValue, assertSchoolState),
  program_level: (queryValue) =>
    getFilterEnumArray<GQL.ProgramLevel>(queryValue, assertProgramLevel),
  only_partnered: getFilterBoolean,
  subindustries: (queryValue) =>
    getFilterEnumArray<GQL.VocationSubindustry>(queryValue, assertSubindustry),
} as const;

// Using FilterKey type to determine whether we should compare equality by wrapping
// in an array or comparing directly.
const isValueEquivalent = (
  filterValue: Filters[FilterKey],
  prevFilterValue: Filters[FilterKey]
): boolean =>
  Array.isArray(filterValue) && Array.isArray(prevFilterValue)
    ? filterValue.every((value, i) => value === prevFilterValue[i])
    : filterValue === prevFilterValue;

// Rules for deciding whether we should set the query param.
export const shouldSetQueryParam = (
  filterKey: FilterKey,
  filterValue: Filters[FilterKey],
  initialFilters?: Filters
): boolean =>
  !(Array.isArray(filterValue) && filterValue.length === 0) &&
  !isValueEquivalent(filterValue, initialFilters?.[filterKey]) &&
  filterValue !== undefined &&
  filterValue !== "" &&
  (filterKey !== "program_duration_days_lte" ||
    (filterKey === "program_duration_days_lte" && !!filterValue)) &&
  filterKey !== "provider_only_partnered" &&
  // Read once from the form-funnel redirect, applied to the query, then dropped
  // from the URL — the duplicate-suppression list shouldn't be visible or
  // shareable. Filter state retains it, so results stay suppressed after the
  // param is stripped (same read-but-don't-persist pattern as only_partnered).
  filterKey !== "provider_exclude_id_in";

export const getFiltersFromQuery = (
  searchParams: { [key: string]: string | string[] | undefined },
  defaultFilters: Filters
): Filters =>
  Object.keys(searchParams)
    .filter((queryKey) => Object.keys(queryParamKeyToFilterKeyMap).includes(queryKey))
    .reduce<Filters>((prev, queryParamKey) => {
      const queryKey = queryParamKey as QueryParamKey;
      const queryValue = cleanQueryParamValue(searchParams[queryKey]);
      const filterKey = getParamFilterKey(queryKey);
      if (!prev?.[filterKey] || defaultFilters[filterKey] !== queryValue) {
        return {
          ...prev,
          [filterKey]: translateQueryParamsToFilters[queryKey]?.(queryValue),
        };
      } else {
        return prev;
      }
    }, {});

export const getQueryParamsFromFilters = (
  filters: Filters | undefined,
  defaultFilters?: Filters
): ParsedUrlQuery =>
  Object.entries(filters ?? {})
    .sort((a, b) => a[0].localeCompare(b[0]))
    .reduce<ParsedUrlQuery>((acc, [filterKey, filterValue]) => {
      const queryKey = Object.entries(queryParamKeyToFilterKeyMap).find(
        ([_queryKey, value]) => value === filterKey
      )?.[0];

      const filterToQueryFunction = translateFiltersToQueryParams[
        filterKey as FilterKey
      ] as FilterTranslatorFunction;

      // If we have a defined translation function, translate the value and then
      // set it in the URL.
      if (filterToQueryFunction) {
        const queryParamEquivalentOfFilterValue = filterToQueryFunction(filterValue);

        if (shouldSetQueryParam(filterKey as FilterKey, filterValue, defaultFilters)) {
          acc[queryKey as keyof QueryParams] = queryParamEquivalentOfFilterValue;
        }
      }

      return acc;
    }, {});

export const getFiltersQueryStringFromQuery = (query: ParsedUrlQuery) => {
  let qs = "";

  Object.keys(queryParamKeyToFilterKeyMap).forEach((filter) => {
    if (query[filter] !== undefined) {
      qs +=
        typeof query[filter] === "string"
          ? `${filter}=${query[filter]}&`
          : (query[filter] as string[]).map((i) => `${filter}=${i}&`).join("");
    }
  });

  return qs ? `?${qs.slice(0, qs.length - 1)}` : qs;
};
